'use client'

import { useState } from 'react'
import {
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Check,
  Copy,
  ArrowRight,
  Headphones,
} from 'lucide-react'
import type { ActionItem, ToolKey, ToolState } from '@/types/radar'
import { EFFORT_LABELS } from '@/lib/radar/scoring'
import { sortActions } from '@/lib/radar/action-priority'
import { buildActionPrompt } from '@/lib/radar/prompt-builder'
import { sanitizeHref } from '@/lib/sanitize'
import { Button } from '@/components/platform/ui'
import { Chip, InstrumentLabel, Panel } from './ui'

/**
 * Action items — the ranked worklist.
 *
 * Redesigned 2026-07-26 against public/Radar - Action Items.html. Presentation
 * only: the ranking, filters, prompt generation, methodology links and the
 * service bridge are unchanged.
 */

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  crawlability: { label: 'Crawlability', color: '#008dcc' },
  'structured-data': { label: 'Structured Data', color: '#8b5cf6' },
  'llm-communication': { label: 'LLM Communication', color: '#22c55e' },
  citations: { label: 'Citations', color: '#F90B8A' },
  community: { label: 'Community', color: '#f97316' },
  aeo: { label: 'AEO', color: '#7c3aed' },
  'answer-engine': { label: 'Citation Test', color: '#dc2626' },
  'source-influence': { label: 'Source Influence', color: '#06b6d4' },
  sov: { label: 'Share of Voice', color: '#a855f7' },
  hallucination: { label: 'Hallucination', color: '#ef4444' },
}

/** Filter chip. Active carries its own hue; inactive is a plain hairline. */
function FilterChip({
  active,
  color,
  onClick,
  children,
}: {
  active: boolean
  color: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-pressed={active}
      className='border px-2.5 py-1 text-xs transition-colors hover:border-hairline-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page'
      style={
        active
          ? { borderColor: color, color }
          : {
              borderColor: 'var(--pm-hairline)',
              color: 'var(--muted-foreground)',
            }
      }
    >
      {children}
    </button>
  )
}

function ActionCard({
  action,
  index,
  tools,
  domain,
  brand,
  entityAssertable = false,
  expanded,
  onToggle,
}: {
  action: ActionItem
  index: number
  tools?: Record<ToolKey, ToolState>
  domain?: string
  brand?: string
  /**
   * Whether the resolved entity may be written into published output. False
   * makes the generated prompt ask for the name instead of asserting ours.
   */
  entityAssertable?: boolean
  expanded: boolean
  onToggle: () => void
}) {
  const [showPrompt, setShowPrompt] = useState(false)
  const [copied, setCopied] = useState(false)
  const [promptText, setPromptText] = useState('')
  const catMeta = CATEGORY_LABELS[action.category]
  const panelId = `action-detail-${action.id}`

  function handleGeneratePrompt() {
    if (showPrompt) {
      setShowPrompt(false)
      return
    }
    if (tools && domain) {
      // `brand` is optional; fall back to the domain so the prompt never
      // interpolates an empty brand name.
      setPromptText(
        buildActionPrompt(
          action,
          tools,
          domain,
          brand || domain,
          entityAssertable
        )
      )
      setShowPrompt(true)
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(promptText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const safeUrl = action.relatedUrl
    ? sanitizeHref(action.relatedUrl, { allowedSchemes: ['https:'] })
    : null

  return (
    <Panel
      as='div'
      className={
        expanded ? undefined : 'transition-colors hover:!border-hairline-strong'
      }
    >
      <button
        type='button'
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className='flex w-full items-start gap-4 px-5 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset sm:px-6'
      >
        {/* Rank. A neutral square well rather than a coloured pill — the chips
            beside it already carry the colour, and a second one competed. */}
        <span
          aria-hidden='true'
          className='flex h-8 w-8 shrink-0 items-center justify-center border border-hairline bg-surface-inset font-mono text-[13px] font-semibold'
        >
          {index + 1}
        </span>

        <span className='flex min-w-0 flex-1 flex-col gap-2'>
          <span className='flex flex-wrap items-center gap-2'>
            <Chip color={catMeta.color}>{catMeta.label}</Chip>
            <Chip color={EFFORT_LABELS[action.effort].color}>
              {EFFORT_LABELS[action.effort].label}
            </Chip>
            {/* Impact chip removed deliberately. It rendered "High Impact"
                from a hand-authored string with no measurement behind it —
                an outcome claim we cannot support. Effort stays because it
                is a stated implementation cost, not a claim about results.
                When an action carries a real priorityScore, that measured
                number is shown instead. */}
            {/* Number.isFinite, not `!= null`: priorityScore is typed
                `number | null` and NaN is a number, so a corrupted persisted
                value would render "NaN". This matches the comparator's own
                tier gate exactly. */}
            {Number.isFinite(action.priorityDetail?.priorityScore) && (
              <Chip color='var(--growth)'>
                {action.priorityDetail!.priorityScore!.toFixed(1)} clicks /
                dev-hour
              </Chip>
            )}
          </span>
          <span className='text-base font-semibold leading-snug'>
            {action.title}
          </span>
        </span>

        {expanded ? (
          <ChevronUp
            size={16}
            aria-hidden='true'
            className='mt-2 shrink-0 text-muted-foreground'
          />
        ) : (
          <ChevronDown
            size={16}
            aria-hidden='true'
            className='mt-2 shrink-0 text-muted-foreground'
          />
        )}
      </button>

      {expanded && (
        <div id={panelId}>
          {/* Indented on desktop to sit under the title, past the rank well. */}
          <div className='grid grid-cols-1 gap-6 border-t border-hairline px-5 py-5 sm:px-6 lg:grid-cols-2 lg:pl-[72px]'>
            <div className='flex flex-col gap-2'>
              <InstrumentLabel tone='inherit' style={{ color: 'var(--cta)' }}>
                Why this matters
              </InstrumentLabel>
              <span className='max-w-[68ch] text-pretty text-sm leading-relaxed text-muted-foreground'>
                {action.why}
              </span>
            </div>
            <div className='flex flex-col gap-2'>
              <InstrumentLabel
                tone='inherit'
                style={{ color: 'var(--primary)' }}
              >
                How to fix
              </InstrumentLabel>
              <span className='max-w-[68ch] text-pretty text-sm leading-relaxed text-muted-foreground'>
                {action.how}
              </span>
            </div>
          </div>

          <div className='flex flex-wrap items-center gap-4 px-5 pb-5 sm:px-6 lg:pl-[72px]'>
            {tools && domain && (
              <Button
                variant='cta'
                size='sm'
                leadingIcon={Sparkles}
                onClick={handleGeneratePrompt}
              >
                {showPrompt ? 'Hide prompt' : 'Generate AI prompt'}
              </Button>
            )}
            {action.methodology && (
              <span className='text-[13px] text-primary'>
                {action.methodology} methodology
              </span>
            )}
            {safeUrl && (
              <a
                href={safeUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-1 text-[13px] text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
              >
                Learn more
                <ExternalLink size={12} aria-hidden='true' />
              </a>
            )}
          </div>

          {showPrompt && promptText && (
            <div className='mx-5 mb-5 border border-hairline bg-surface-inset sm:mx-6 lg:ml-[72px]'>
              <div className='flex items-center justify-between gap-3 border-b border-hairline px-3 py-2'>
                <InstrumentLabel
                  tone='inherit'
                  style={{ color: 'var(--primary)' }}
                >
                  Copy this prompt into your AI tool
                </InstrumentLabel>
                <Button
                  variant='secondary'
                  size='xs'
                  leadingIcon={copied ? Check : Copy}
                  onClick={handleCopy}
                  className={copied ? 'border-growth/50 text-growth' : ''}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <textarea
                readOnly
                aria-label='Generated AI prompt'
                value={promptText}
                className='w-full resize-none bg-transparent p-3 font-mono text-[11px] leading-relaxed text-muted-foreground focus:outline-none'
                style={{ minHeight: 200, maxHeight: 400 }}
                rows={Math.min(20, promptText.split('\n').length + 2)}
              />
            </div>
          )}
        </div>
      )}
    </Panel>
  )
}

export function ActionsPanel({
  actions,
  tools,
  domain,
  brand,
  entityAssertable = false,
}: {
  actions: ActionItem[]
  tools?: Record<ToolKey, ToolState>
  domain?: string
  brand?: string
  /** See ActionCard — defaults to false so an un-updated caller cannot assert. */
  entityAssertable?: boolean
}) {
  const [filterCat, setFilterCat] = useState<string | null>(null)
  const [filterEffort, setFilterEffort] = useState<string | null>(null)
  // Which cards are open. `null` means untouched, in which case the top-ranked
  // action is open — derived rather than stored so that changing a filter
  // re-opens whatever is ranked first under that filter. State lives here, not
  // in the card, because a card keyed by action.id keeps its own state when
  // filtering merely moves it up the list.
  const [expandedIds, setExpandedIds] = useState<Set<string> | null>(null)

  // Canonical order at the view-model boundary. generateActions already
  // sorts, but this panel must not depend on that: a persisted snapshot or
  // a future API path could hand it an unsorted array.
  const filtered = sortActions(actions)
    .filter(a => !filterCat || a.category === filterCat)
    .filter(a => !filterEffort || a.effort === filterEffort)

  const categories = [...new Set(actions.map(a => a.category))]

  const topId = filtered[0]?.id
  const isExpanded = (id: string) =>
    expandedIds === null ? id === topId : expandedIds.has(id)

  function toggleExpanded(id: string) {
    setExpandedIds(prev => {
      // Seed from the derived default so the first toggle does not silently
      // collapse the top card alongside the one that was clicked.
      const next = new Set(prev ?? (topId ? [topId] : []))
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-baseline gap-3'>
        <h2 className='font-display text-[25px] font-semibold tracking-[-0.02em]'>
          Action items
        </h2>
        <Chip color='var(--primary)'>{filtered.length}</Chip>
      </div>

      <div className='flex flex-wrap items-center gap-1.5'>
        <InstrumentLabel tone='muted' className='mr-1'>
          Category
        </InstrumentLabel>
        <FilterChip
          active={!filterCat}
          color='var(--primary)'
          onClick={() => setFilterCat(null)}
        >
          All
        </FilterChip>
        {categories.map(c => {
          const meta = CATEGORY_LABELS[c]
          return (
            <FilterChip
              key={c}
              active={filterCat === c}
              color={meta.color}
              onClick={() => setFilterCat(filterCat === c ? null : c)}
            >
              {meta.label}
            </FilterChip>
          )
        })}

        <span
          aria-hidden='true'
          className='mx-2 hidden h-5 w-px bg-hairline sm:block'
        />

        <InstrumentLabel tone='muted' className='mr-1'>
          Effort
        </InstrumentLabel>
        {(['low', 'medium', 'high'] as const).map(e => (
          <FilterChip
            key={e}
            active={filterEffort === e}
            color={EFFORT_LABELS[e].color}
            onClick={() => setFilterEffort(filterEffort === e ? null : e)}
          >
            {EFFORT_LABELS[e].label}
          </FilterChip>
        ))}
      </div>

      <div className='flex flex-col gap-3'>
        {filtered.map((action, i) => (
          <div key={action.id} className='flex flex-col gap-3'>
            <ActionCard
              action={action}
              index={i}
              tools={tools}
              domain={domain}
              brand={brand}
              entityAssertable={entityAssertable}
              expanded={isExpanded(action.id)}
              onToggle={() => toggleExpanded(action.id)}
            />
            {/* Service bridge after every 3rd item */}
            {(i + 1) % 3 === 0 && i < filtered.length - 1 && (
              <Panel
                as='div'
                tone='action'
                className='flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:px-6'
              >
                <Headphones
                  size={16}
                  aria-hidden='true'
                  className='shrink-0'
                  style={{ color: 'var(--cta)' }}
                />
                <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                  <span className='text-sm font-semibold'>
                    Too many issues to fix yourself?
                  </span>
                  <span className='max-w-[68ch] text-[13px] leading-relaxed text-muted-foreground'>
                    Our team implements these fixes for you, typically resolving
                    all action items within 2 weeks.
                  </span>
                </div>
                <a
                  href='https://www.pixelmojo.io/contact-us'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex h-9 shrink-0 items-center gap-1.5 bg-[var(--cta-fill)] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#c3086e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
                >
                  Talk to us
                  <ArrowRight size={14} aria-hidden='true' />
                </a>
              </Panel>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <Panel className='px-6 py-10 text-center'>
            <span className='block text-sm text-muted-foreground'>
              No actions match the current filters.
            </span>
          </Panel>
        )}
      </div>
    </div>
  )
}
